import { format } from "date-fns";
import { TICK_LABEL_FORMAT, GRID_LINE_COUNT } from "./constants";
import { TimePeriod, ChartDomain, ChartDatum, CursorValue } from "./types";

export const getTimestamp = (
  value: Date | number | string | null | undefined
): number | null => {
  if (value instanceof Date) {
    const timestamp = value.getTime();

    return Number.isNaN(timestamp) ? null : timestamp;
  }

  if (typeof value === "number") {
    return Number.isFinite(value) ? value : null;
  }

  if (typeof value === "string") {
    const timestamp = new Date(value).getTime();

    return Number.isNaN(timestamp) ? null : timestamp;
  }

  return null;
};

export const formatDateTick = (
  tick: Date | number | string,
  timePeriod: TimePeriod
): string => {
  const timestamp = getTimestamp(tick);

  if (timestamp === null) return "";

  return format(new Date(timestamp), TICK_LABEL_FORMAT[timePeriod]);
};

// Returns `count` dates centered within equal slices of [start, end].
export const centeredTicks = (
  start: Date,
  end: Date,
  count: number
): Date[] => {
  const startTime = start.getTime();
  const endTime = end.getTime();

  if (count <= 0) return [];
  if (count === 1 || startTime === endTime) {
    return [new Date((startTime + endTime) / 2)];
  }

  const step = (endTime - startTime) / count;

  return Array.from(
    { length: count },
    (_, index) => new Date(startTime + step * (index + 0.5))
  );
};

export const getTickValues = (
  chartDomain: ChartDomain | undefined,
  tickCount: number = 12
): Date[] => {
  if (!chartDomain) return [];

  const [firstDate, lastDate] = chartDomain.x;

  return centeredTicks(firstDate, lastDate, tickCount);
};

// Evenly spaced y values spanning the full chart box above the tick labels,
// inclusive of both edges. The last line lands on the x-axis boundary.
export const getGridLinePositions = (
  chartHeight: number,
  bottomPadding: number,
  strokeWidth: number
): number[] => {
  const topY = strokeWidth / 2;
  const bottomY = chartHeight - bottomPadding - strokeWidth / 2;

  return Array.from(
    { length: GRID_LINE_COUNT },
    (_, index) => topY + ((bottomY - topY) * index) / (GRID_LINE_COUNT - 1)
  );
};

export const getNearestDatum = (
  chartData: ChartDatum[],
  cursorValue: CursorValue
): ChartDatum | null => {
  const cursorTimestamp = getTimestamp(cursorValue);

  if (cursorTimestamp === null || chartData.length === 0) return null;

  return chartData.reduce<ChartDatum>((closestDatum, datum) => {
    const closestDistance = Math.abs(
      closestDatum.x.getTime() - cursorTimestamp
    );
    const datumDistance = Math.abs(datum.x.getTime() - cursorTimestamp);

    return datumDistance < closestDistance ? datum : closestDatum;
  }, chartData[0]);
};
